'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// BACKEND INTEGRATION: Replace with POST /api/auth/login
const DEMO_ACCOUNTS = [
  {
    role: 'Patient',
    name: 'Lars de Vries',
    email: 'lars.devries@gmail.com',
    password: 'Waell2026!',
    redirect: '/patient-dashboard',
    color: 'bg-teal-100 text-teal-700',
    initials: 'LV',
    avatarColor: 'from-teal-400 to-teal-600',
  },
  {
    role: 'Medical Assistant',
    name: 'Nina Bakker',
    email: 'nina.bakker@waell.nl',
    password: 'Waell2026!',
    redirect: '/medical-assistant-dashboard',
    color: 'bg-blue-100 text-blue-700',
    initials: 'NB',
    avatarColor: 'from-blue-400 to-blue-600',
  },
  {
    role: 'Doctor',
    name: 'Dr. Jan Hoekstra',
    email: 'j.hoekstra@waell.nl',
    password: 'Waell2026!',
    redirect: '/doctor-dashboard',
    color: 'bg-indigo-100 text-indigo-700',
    initials: 'JH',
    avatarColor: 'from-indigo-400 to-indigo-600',
  },
  {
    role: 'Operations',
    name: 'Roos Mulder',
    email: 'roos.mulder@waell.nl',
    password: 'Waell2026!',
    redirect: '/operations-dashboard',
    color: 'bg-orange-100 text-orange-700',
    initials: 'RM',
    avatarColor: 'from-orange-400 to-orange-600',
  },
  {
    role: 'Pharmacy',
    name: 'Benu Apotheek',
    email: 'pharmacy@benu.nl',
    password: 'Waell2026!',
    redirect: '/pharmacy-dashboard',
    color: 'bg-purple-100 text-purple-700',
    initials: 'BA',
    avatarColor: 'from-purple-400 to-purple-600',
  },
  {
    role: 'Compliance / Admin',
    name: 'Compliance Admin',
    email: 'compliance@waell.nl',
    password: 'Waell2026!',
    redirect: '/compliance-dashboard',
    color: 'bg-slate-100 text-slate-700',
    initials: 'CA',
    avatarColor: 'from-slate-400 to-slate-600',
  },
];

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const handleCopy = async (value: string, field: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const fillCredentials = (account: typeof DEMO_ACCOUNTS[0]) => {
    setValue('email', account.email);
    setValue('password', account.password);
    setAuthError(null);
    toast.info(`Filled credentials for ${account.role}`, { description: account.name });
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError(null);
    // BACKEND INTEGRATION: POST /api/auth/login with { email, password, rememberMe }
    await new Promise((r) => setTimeout(r, 1200));

    const matched = DEMO_ACCOUNTS.find(
      (a) => a.email === data.email && a.password === data.password
    );

    if (matched) {
      toast.success(`Welcome, ${matched.name}!`, {
        description: `Redirecting to ${matched.role} dashboard…`,
      });
      setTimeout(() => router.push(matched.redirect), 800);
    } else {
      setAuthError('Invalid credentials — use one of the demo accounts below to sign in.');
    }
    setIsLoading(false);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[hsl(var(--foreground))]">Welcome back</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Sign in to your Waell portal
        </p>
      </div>

      {/* Auth error */}
      {authError && (
        <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-3.5 animate-slide-up">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{authError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-1.5">
            Email address
          </label>
          <input
            type="email"
            autoComplete="email"
            placeholder="uw@email.nl"
            className={`input-field ${errors.email ? 'input-error' : ''}`}
            {...register('email', {
              required: 'Email address is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-semibold text-[hsl(var(--foreground))]">
              Password
            </label>
            <a
              href="#"
              className="text-xs text-[#0F6B5E] font-medium hover:underline"
              onClick={(e) => { e.preventDefault(); toast.info('Password reset email sent — check your inbox.'); }}
            >
              Forgot password?
            </a>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={`input-field pr-11 ${errors.password ? 'input-error' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-600">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2.5">
          <input
            id="rememberMe"
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-[#0F6B5E] focus:ring-[#0F6B5E] cursor-pointer"
            {...register('rememberMe')}
          />
          <label htmlFor="rememberMe" className="text-sm text-[hsl(var(--muted-foreground))] cursor-pointer select-none">
            Keep me signed in for 30 days
          </label>
        </div>

        {/* 2FA placeholder notice */}
        <div className="flex items-start gap-2 bg-[#0F6B5E]/5 border border-[#0F6B5E]/20 rounded-xl p-3">
          <svg className="w-4 h-4 text-[#0F6B5E] flex-shrink-0 mt-0.5" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M8 7.5v4M8 5.5h.01" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <p className="text-xs text-[#0F6B5E]/80">
            Two-factor authentication is enabled on this account. A code will be sent to your phone after sign-in. <span className="font-semibold">(2FA placeholder — not active in MVP)</span>
          </p>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 text-base"
          style={{ minWidth: '200px' }}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Signing in…</span>
            </>
          ) : (
            'Sign in to Waell'
          )}
        </button>
      </form>

      {/* Demo credentials box */}
      <div className="mt-6 bg-[#F8FAFB] border border-[hsl(var(--border))] rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))]">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
              Demo credentials
            </span>
            <span className="bg-[#0F6B5E] text-white text-xs font-bold rounded-full px-1.5 py-0.5 leading-none">
              {DEMO_ACCOUNTS.length} roles
            </span>
          </div>
        </div>

        <div className="divide-y divide-[hsl(var(--border))]">
            {/* Shared password note */}
            <div className="px-4 py-2.5 bg-amber-50 flex items-center justify-between gap-3">
              <p className="text-xs text-amber-700">
                All accounts use password: <span className="font-mono font-semibold">Waell2026!</span>
              </p>
              <button
                onClick={() => handleCopy('Waell2026!', 'shared-password')}
                className="text-amber-600 hover:text-amber-800 transition-colors flex-shrink-0"
                aria-label="Copy shared password"
              >
                {copiedField === 'shared-password' ? (
                  <Check className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            </div>

            {DEMO_ACCOUNTS.map((account) => (
              <div key={account.role} className="px-4 py-3 flex items-center gap-3 bg-white hover:bg-[#F8FAFB] transition-colors">
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${account.avatarColor} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}>
                  {account.initials}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-md ${account.color}`}>
                      {account.role}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-[hsl(var(--foreground))] truncate">{account.name}</p>
                  <p className="font-mono text-xs text-[hsl(var(--muted-foreground))] truncate">{account.email}</p>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleCopy(account.email, `email-${account.role}`)}
                    className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[#0F6B5E] transition-colors"
                    aria-label={`Copy ${account.role} email`}
                    title="Copy email"
                  >
                    {copiedField === `email-${account.role}` ? (
                      <Check className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <button
                    onClick={() => fillCredentials(account)}
                    className="text-xs font-semibold text-[#0F6B5E] hover:underline px-2 py-1 rounded-lg hover:bg-[#0F6B5E]/5 transition-colors"
                  >
                    Use
                  </button>
                </div>
              </div>
            ))}
          </div>
      </div>

      <p className="mt-5 text-center text-sm text-[hsl(var(--muted-foreground))]">
        Don&apos;t have an account?{' '}
        <button
          onClick={onSwitchToRegister}
          className="text-[#0F6B5E] font-semibold hover:underline"
        >
          Start your intake
        </button>
      </p>
    </div>
  );
}